/**
 *  @file       shutdown.js     
 *
 *              Generic code to handle the trapping of uncaught promise
 *              rejections and POSIX signals in network daemons, with
 *              error reporting propagation to the clients.
 *
 *              There are three kinds of shutdown operations:
 *              1 - graceful tasks: these are always invoked and tell the graceful
 *                  shutdown handler whether or not they were successful. Async
 *                  functions are supported and functions returning promises are
 *                  awaited. Unsuccessful shutdown attempts return false.
 *
 *              2 - forced tasks: these are invoked if the graceful tasks do not
 *                  complete after a specified time. The process will exit in the
 *                  same pass of the event loop as the execution of the forced tasks.
 *
 *              3 - shutdown functions: these are always invoked and their return
 *                  values are ignored; similar to POSIX atexit()
 *
 *              Shutdown functions and forced shutdowns are hooked from process.exit,
 *              and as such always get invoked on exit if registered.  In order to
 *              perform a graceful shutdown, the exports.manage() should be used to
 *              wrap the program's main function OR the exports.shutdown() will need
 *              to be explicitly invoked.
 *
 *              IMPORTANT: Do not call process.exit() from a shutdown function or task.
 *                         Use process.exitCode to set a non-zero exit code. This 
 *                         library will not override a non-zero exit code from the 
 *                         application program.
 *
 *  @author     Wes Garland, wes@kingsds.network
 *  @date       July 2018, May 2021
 */
'use strict';

const process = require('process');
const { leafMerge } = require('dcp/utils');
const { assert } = require('dcp/common/dcp-assert');
const { setBackoffInterval, clearBackoffInterval } = require('dcp/common/dcp-timers');
const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');

const taskRegistry = [];
const registeredAtFunctions = [];
const debugging = require('dcp/debugging').scope('shutdown');

var timeDilation = 1;
try
{
  if (requireNative('module')._cache.niim instanceof requireNative('module').Module)
  {
    timeDilation = 10;
    debugging('timeDilation') && console.debug(`053: Running inside niim; timeDilation is ${timeDilation}`);
  }
}
catch (error)
{
  debugging('timeDilation') && console.debug(`058: Failed to requireNative('module'); probably not running in Node`);
}

const defaultOptions = {
  gracefulTimeout: "15.0",
  backoffFactor:   1.1,
  baseInterval:    1000,
  maxInterval:     3000
};

/**
 * Analogue of POSIX atexit(2). Invokes a function when the process is exiting.
 * Ideally, this is what process.on('exit') would do, but it doesn't fire in
 * the case of default signal handlers.
 */
exports.atExit = function shutdown$$atExit(fn)
{
  require('./signal-handler').init();
  process.on('dcpExit', fn);
}

exports.atExitCancel = function shutdown$$atExit(fn)
{
  process.off('dcpExit', fn);
}

/** Register a task which is to be executed during the shutdown of a server.
 *
 *  @param {Function} taskFn              The function to execute. When invoked, this function receives
 *                                        a single argument, which is the reason for the shutdown (the
 *                                        why object from inside the server manager).  The taskFn function
 *                                        must return true to indicate that its job has been completed;
 *                                        otherwise it will be tried again (and again) during shutdown.
 *                                        This function /must/ behave in a 'synchronous' (blocking) manner.
 *                                        The taskFn function will not be invoked until after we have tried
 *                                        to close all of the client sockets.
 *
 *  @param {boolean} [gracefulOnly=false] Execute only when performing graceful shutdown
 *  @param {boolean} [forcedOnly=false]   Execute only when performing forced shutdown
 */
exports.registerTask = function shutdown$$registerTask(taskFn, gracefulOnly, forcedOnly)
{
  var registration = {
    fn:       taskFn,
    graceful: !forcedOnly,
    forced:   !gracefulOnly
  };

  debugging() && console.debug(`shutdown: registered task ${taskFn.name} ${gracefulOnly ? '- graceful only' : ''} ${forcedOnly ? '- forced only' : ''}`);
  assert(!(forcedOnly && gracefulOnly));
  
  taskRegistry.push(registration);
}

/** Unregister all previously-registered shutdown tasks matching taskFn.
 *
 *  @see exports.registerTask
 *
 *  @param      taskFn          The function which was previously registered
 *  @param      gracefulOnly    Only unregister the graceful-shutdown invocation of this function
 *  @param      forcedOnly      Only unregister the forced-shutdown invocation of this function
 *
 *  @returns    true if the task was located in the task registry
 */
exports.unregisterTask = function shutdown$$unregisterTask(taskFn, gracefulOnly, forcedOnly)
{
  var ret = false;

  debugging() && console.debug(`shutdown: unregistering task ${taskFn.name} ${gracefulOnly ? '- graceful only' : ''} ${forcedOnly ? '- forced only' : ''}`);

  for (let i=0; i < taskRegistry.length; i++)
  {
    let registration = taskRegistry[i];

    if (registration.fn !== taskFn)
      continue;

    if (gracefulOnly)
      registration.graceful = false;
    if (forcedOnly)
      registration.forced = false;
    
    if ((!registration.forced && !registration.graceful) || (!gracefulOnly && !forcedOnly))
      taskRegistry.splice(i--, 1);

    debugging() && console.debug(`shutdown: unregistered ${taskFn.name}`);

    ret = true;
  }

  return ret;
}

/**
 * Shutdown reasons can be varied and are intended to be convenient to use for the caller
 * from whatever context/information happens to be at hand.
 *
 * Once this function returns, the new reason should be something we can handle elegantly
 * from inside this code or inside a shutdown task.  The following properties will always
 * be defined:
 *   why:    root cause of shutdown decision (eg SIGINT); should be short and unique so that
 *           shutdown tasks can inspect it to make choices
 *   mesage: root cause of shutdown decision in human-readable text.
 */
function fixReason(reason)
{
  if (typeof reason === 'string')
    reason = { why: reason, message: reason };
  if (!reason.why && reason instanceof Error)
    reason.why = reason.name;
  if (!reason.why && !reason.message)
  {
    reason.why = 'unknown';
    const stack = new Error().stack;
    if (stack)
      reason.message = stack.split('\n').slice(2).join('\n');
  }
  if (!reason.why)
    reason.why = reason.message;
  if (!reason.message)
    reason.message = reason.why;

  return reason;
}

/**
 * Invoke each graceful shutdown handler once, await their results, unregistering if they succeed. A "failing" 
 * task causes this function to bail without processing any further tasks. This allows us, for example, to 
 * implement a daemon that can stay alive during heavy traffic and then bounce when there is a break in user
 * activity.
 *
 * @param {Object}       reason          Reason object to pass to shutdown task
 * @returns true if no handlers returned false.
 */
async function invokeGracefulShutdownTasks(reason)
{
  reason = fixReason(reason);
  debugging() && console.debug('Graceful shutdown -', reason.message);
  
  while(taskRegistry.length)
  {
    let registration = taskRegistry[taskRegistry.length - 1];
    let taskFn = registration.fn;

    if (!registration.graceful)
      continue;
    
    try
    {
      let notOk = await taskFn(reason) === false;
      debugging() && console.debug(`shutdown: graceful task ${taskFn.name} ${notOk ? 'did not run' : 'ran'} ok`);
      if (notOk)
        return false;
    }
    catch(e)
    {
      if (!(e instanceof Error))
        console.warn(`Warning: Shutdown task ${taskFn.name} rejected with type=${typeof e}, which is not an instanceof Error`);
      debugging() && console.debug(`shutdown: graceful task ${taskFn.name} failed (will retry); ${e && e.message}`);
      return false;
    }
    
    exports.unregisterTask(taskFn);
    debugging() && console.log('debugging: tasks left:', taskRegistry.length);
  }

  return true;
}

/* Invoke each forced shutdown handler once.
 *
 * @param {Object}       reason          Reason object to pass to shutdown task
 */
function invokeForcedShutdownTasks(reason)
{
  reason = fixReason(reason);
  debugging() && console.debug('shutdown: starting forced shutdown -', reason);

  for (let registration of taskRegistry)
  {
    let taskFn = registration.fn;
    
    if (registration.forced)
    {
      try
      {
        taskFn(reason);
        debugging() && console.debug(`shutdown: ran forced task ${taskFn.name}`);
      }
      catch(e)
      {
        console.warn(`shutdown: forced shutdown task ${taskFn.name} failed`, e);
      }
    }

    exports.unregisterTask(taskFn);
    debugging() && console.log('debugging: tasks left:', taskRegistry.length);
  }
}

/** 
 * Register a shutdown function.
 *
 * These functions are invoked once during the shutdown process. They are different from shutdown 
 * tasks in that they are fire-and-forget rather than monitored and potentially retried. Functions are
 * run in LIFO order, and before any of the shutdown tasks.
 *
 * This function hooks process.exit, so the shutdown functions and the forced shutdown tasks are
 * run, if registered, regardless of whether or not this is a managed process.
 *
 * @param {function}    fn      a function to run at shutdown
 */ 
exports.atShutdown = function shutdown$$atShutdown(fn)
{
  registeredAtFunctions.push(fn);
  debugging() && console.debug('shutdown: registered function', fn.name || '(anonymous)');

  if (exports.atShutdown.ran !== true)
  {
    exports.atShutdown.ran = true;
    process.on('dcpExit', function atShutdownHandler() {
      debugging() && console.debug('shutdown: atShutdownHandler');
      invokeForcedShutdownTasks('process exit');
      invokeShutdownFunctions('process exit');
    });
  }
}

function invokeShutdownFunctions(reason)
{
  var fn;
  
  reason = fixReason(reason);
  debugging() && console.debug(`shutdown: running ${registeredAtFunctions.length} shutdown functions (${reason.message}, exitCode=${process.exitCode})`);

  while ((fn = registeredAtFunctions.pop()))
  {
    try
    {
      fn();
      debugging() && console.debug('shutdown: ran shutdown function', fn.name);
    }
    catch (error)
    {
      console.error(`Error running shutdown function ${fn.name}:`, error);
    }
    debugging() && console.log('debugging: functions left:', registeredAtFunctions.length);
  }
}

/**
 * Perform the clean shutdown proceedure.
 *
 * This function will eventually invoke process.exit() with one of the following exit codes, 
 * provided no non-zero value was specified for process.exitCode before this function was called:
 *
 * - 0  => clean exit
 * - 50 => forced shutdown after graceful attempt failed
 * - 51 => error during shutdown
 * - 52 => shutdown because managed function threw an exception
 * - 53 => shutdown because managed function had unhandled rejection
 * - 60 => impossible code block reached
 * - 61...82 => reserved for signal handlers; suggest: 60 + signal #
 *
 * WARNING - this function should be awaited or the very last thing your daemon calls. Otherwise,
 *           your daemon can wind up trying to "do work" during shutdown, which might not go well.
 * 
 * A "keepalive" timer is started at the beginning of this function with the express purpose of
 * preventing Node.js from exiting due to an empty/unref event loop once this process has begun.
 *
 * @param {string|object}       reason          the reason we are shutting down
 * @param {object}              options         overrides for dcpConfig.daemons.shutdown
 * @param {boolean}             force           if true, go straight to forced shutdown
 */
exports.shutdown = async function shutdown (reason, force, options = {})
{
  var nodeKeepalive;
  var backoffHnd;

  reason = fixReason(reason);
  options = leafMerge(defaultOptions, (typeof dcpConfig !== 'undefined' ? dcpConfig.daemons.shutdown : {}), options);
  debugging() && console.debug(`shutdown: start; ${taskRegistry.length} tasks; ${registeredAtFunctions.length} at-functions;`, reason.why, options);
  
  if (timeDilation !== 1)
  {
    /* slow things down in the debugger */
    options.gracefulTimeout *= timeDilation;
    options.baseInterval    *= timeDilation;
    options.maxInterval     *= timeDilation;
  }
  
  function forceShutdown()
  {
    debugging() && console.debug('shutdown: beginning forced shutdown phase');
  
    try
    {
      clearBackoffInterval(backoffHnd);
    }
    catch(e){};
    
    try
    {
      invokeForcedShutdownTasks(reason);
      invokeShutdownFunctions(reason);
    }
    catch(e)
    {
      console.error(e);
    }

    process.exit();
  }

  if (exports.shutdown.timer)
    return false; /* ignore multi-trigger */

  try
  {
    if (!options.quiet)
      console.log(`Shutting down, ${reason.message}` + (force ? ' (forced)' : ''));
    if (reason instanceof Error && !options.quiet)
      console.error('Shutdown reason stack:', reason.stack || '');

    const stack = new Error().stack;
    reason  = reason || (stack ? stack.split('\n')[1] : '<unknown, no stack>');
    if (typeof reason === 'string')
      reason = { message: reason };

    /**
     * Set up a forced shutdown timer that fires if the graceful shutdown fails
     * or if a shutdown function never resolves. Then create a dummy timer which
     * keeps node from exiting due to an empty event loop, just in case, so that
     * we can ensure we have control of the shutdown process.
     */
    debugging() && console.debug('shutdown: graceful timeout is', options.gracefulTimeout + 's');
    function gracefulTimeoutElapsed() {
      process.exitCode = 50;
      forceShutdown();
    }
    exports.shutdown.timer = setTimeout(gracefulTimeoutElapsed, 1000 * options.gracefulTimeout).unref();
    nodeKeepalive = setTimeout(Function, 1 << 30 /* "infinity" */);

    if (force === true)
    {
      forceShutdown();
      process.exit(60);
    }

    /* Try running the graceful shutdown tasks on an exponential backoff until no
     * tasks fail; when this happens, clear the forced shutdown timer and exit.
     * Note that this promise is purposefully never resolved. Process.exit is 
     * invoked via process.nextTick so that anything else on the event loop can
     * unwind but with no danger that new events will be handled.
     */
    await new Promise(function gracefulShutdown(resolve) {
      var backoffHnd;

      async function gracefulTry()
      {
        if (gracefulTry.trying)
          return;

        gracefulTry.trying = true;
        try
        {
          let res = await invokeGracefulShutdownTasks(reason);
          gracefulTry.trying = false;
          if (res === false)
            return; // not dead yet
          debugging() && console.log('shutdown: finished graceful shutdown tasks');
          
          clearTimeout(nodeKeepalive);
          clearBackoffInterval(backoffHnd);
          clearTimeout(exports.shutdown.timer);
          invokeShutdownFunctions('post-graceful');
          resolve(); /* drop to process.exit in finally block */
        }
        finally
        {
          gracefulTry.trying = false;
        }
      }
      
      backoffHnd = setBackoffInterval(gracefulTry, options);
      gracefulTry();
    });
  }
  catch(error)
  {
    console.log(error);
    if (!process.exitCode)
      process.exitCode = 51;
  }
  finally
  {
    process.exit();
  }
}

/**
 * Manage a process with the default shutdown hooks.
 *
 * @return an Object with functions corresponding to the different shutdown handlers, 
 *         sigInt, sigQuit, sigTerm, uncaughtException and unhandledRejection. During 
 *         shutdown trigger, this object is used as a jump table, so the object can be 
 *         used to either disable triggers or replace them as needed.
 */
exports.manageProcess = function shutdown$$manageProcess()
{
  const shutdown = exports.shutdown;
  var jumpTable;

  function unhandledRejection(error, promise)
  {
    if (!(error instanceof Error))
      error = new Error('Unhandled Rejection:'  + error);
    process.exitCode = process.exitCode || 53;
    console.error('\nunhandled rejection:', error, 'at:', promise);
    error.why = 'unhandledRejection';
    shutdown(error, true);
  }

  function uncaughtException(error, origin)
  {
    process.exitCode = process.exitCode || 53;
    console.error('\nuncaught exception:', error);
    error.why = 'uncaughtException';
    shutdown(error, true);
  }

  function sigInt()
  {
    process.exitCode = process.exitCode || 63;
    shutdown({why: 'SIGINT', message: 'Trapped signal 2'});
    jumpTable.sigInt = doubleTap;
  }

  function sigQuit()
  {
    process.exitCode = process.exitCode || 70;
    shutdown({why: 'SIGQUIT', message: 'Trapped signal 10'});
    jumpTable.sigQuit = doubleTap;
  }

  function sigTerm()
  {
    process.exitCode = process.exitCode || 75;
    shutdown({why: 'SIGTERM', message: 'Trapped signal 15'});
    jumpTable.sigTerm = doubleTap;
  }

  function doubleTap()
  {
    exports.shutdown.timer._onTimeout(); /* expire the graceful shutdown right away */
  }
  
  delete exports.manageProcess; /* don't call twice! */
  
  process.on('unhandledRejection', (...a) => jumpTable.unhandledRejection(...a));
  process.on('uncaughtException',  (...a) => jumpTable.uncaughtException (...a));
  process.on('SIGINT',             (...a) => jumpTable.sigInt            (...a));
  process.on('SIGQUIT',            (...a) => jumpTable.sigQuit           (...a));
  process.on('SIGTERM',            (...a) => jumpTable.sigTerm           (...a));

  jumpTable = { unhandledRejection, uncaughtException, sigInt, sigQuit, sigTerm };
  return jumpTable;
}

/** 
 * Begin managing a program's shutdown.
 *
 * @param {function}  func      the program's main function - shutdown begins automatically when it returns or
 *                              the promise it returns resolves or rejects.
 * @param {array}     argv      arguments to pass to func
 *
 * @returns the value returned by func.apply(null, argv);, unless it is a Promise, in which case we return a
 *          Promise which awaits that value.
 */
exports.manage = async function shutdown$$manage(func, argv, options)
{
  const shutdown = exports.shutdown;
  let ret;

  exports.manageProcess();

  debugging() && console.debug('shutdown: managing function', func.name);
  try
  {
    ret = func.apply(null, argv || []);
  }
  catch(e)
  {
    process.exitCode = process.exitCode || 52;
    console.error(e);
    await shutdown({why: 'uncaughtException', message: e.message}).catch(process.exit);
    process.exit(60);
  }

  debugging() && console.debug(`shutdown: ${func.name} was ${ret instanceof Promise ? 'async' : 'sync'}`);
  if (!(ret instanceof Promise))
  {
    await shutdown({why: 'normal exit'})
    process.exit(60);
  }
  
  try
  {
    /* ret is a Promise => func is async */
    await ret
      .then((result) => shutdown({why: 'normal exit', message: 'normal exit (async)'}))
      .catch((error) => shutdown({why: `unhandled rejection ${error.message}`}))
  }
  catch(error)
  {
    console.log(error);
  }

  process.exit(60);
}
